import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarProvider,
  SidebarInset,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '../ui/Sidebar';
import { Separator } from '../ui/Separator';
import { SidebarUserFooter } from './SidebarUserFooter';
import type { AppMode } from './SidebarUserFooter';

// ============================================================================
// Types
// ============================================================================

export interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Only show this item when condition is true (defaults to true) */
  show?: boolean;
  /** Optional badge count to display next to the title */
  badge?: number;
}

export interface NavGroup {
  title?: string;
  items: NavItem[];
}

export interface AppLayoutProps {
  children: React.ReactNode;
  /** Content to render in the sidebar header (logo, switcher, etc.) */
  headerSlot: React.ReactNode;
  /** Navigation groups to render in the sidebar */
  navGroups: NavGroup[];
  /** Current app mode - determines footer behavior */
  mode: AppMode;
  /** Optional content to render above the user footer */
  preFooterSlot?: React.ReactNode;
  /** Title shown in the site header */
  siteHeaderTitle?: string;
  /** Optional actions to render in the site header */
  headerActions?: React.ReactNode;
}

// Re-export AppMode for convenience
export type { AppMode };

// ============================================================================
// Internal Components
// ============================================================================

interface AppNavGroupProps {
  group: NavGroup;
  onNavigate: (url: string) => void;
}

function AppNavGroup({ group, onNavigate }: AppNavGroupProps) {
  const location = useLocation();

  const visibleItems = group.items.filter(item => item.show !== false);
  
  if (visibleItems.length === 0) return null;

  return (
    <SidebarGroup>
      {group.title && <SidebarGroupLabel>{group.title}</SidebarGroupLabel>}
      <SidebarGroupContent>
        <SidebarMenu>
          {visibleItems.map((item) => {
            const isActive = location.pathname === item.url || 
              (item.url !== '/' && location.pathname.startsWith(item.url + '/'));
            
            return (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton 
                  asChild
                  isActive={isActive}
                  size="lg"
                >
                  <Link 
                    to={item.url}
                    onClick={(e) => {
                      // Handle normal left-clicks
                      if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                        e.preventDefault();
                        onNavigate(item.url);
                      }
                    }}
                    aria-current={isActive ? 'page' : undefined}
                    className="flex items-center gap-2 w-full"
                  >
                    <item.icon className="h-4 w-4" />
                    <span className="flex-1">{item.title}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="ml-auto bg-destructive text-destructive-foreground text-xs font-medium px-2 py-0.5 rounded-full min-w-[1.5rem] text-center">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

interface AppSidebarProps {
  headerSlot: React.ReactNode;
  navGroups: NavGroup[];
  mode: AppMode;
  preFooterSlot?: React.ReactNode;
}

function AppSidebar({ headerSlot, navGroups, mode, preFooterSlot }: AppSidebarProps) {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();

  const handleNavigate = (url: string) => {
    if (isMobile) {
      setOpenMobile(false);
    }
    navigate(url);
  };

  return (
    <Sidebar collapsible="offcanvas">
      <SidebarHeader>
        {headerSlot}
      </SidebarHeader>
      
      <SidebarContent>
        {navGroups
          .map((group) => {
            const visibleItems = group.items.filter(item => item.show !== false);
            return visibleItems.length > 0 ? group : null;
          })
          .filter((group): group is NavGroup => group !== null)
          .map((group, index) => (
            <React.Fragment key={group.title || `group-${index}`}>
              {index > 0 && <Separator className="my-2" />}
              <AppNavGroup group={group} onNavigate={handleNavigate} />
            </React.Fragment>
          ))}
      </SidebarContent>
      
      {preFooterSlot}
      
      <SidebarFooter>
        <SidebarUserFooter mode={mode} />
      </SidebarFooter>
    </Sidebar>
  );
}

interface AppSiteHeaderProps {
  title?: string;
  actions?: React.ReactNode;
}

function AppSiteHeader({ title, actions }: AppSiteHeaderProps) {
  // Don't render header at all if there's no title and no actions
  if (!title && !actions) {
    return null;
  }
  
  return (
    <header className="sticky top-0 z-40 bg-background flex h-14 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear">
      <div className="flex w-full items-center gap-2 px-4">
        <SidebarTrigger className="-ml-1" />
        {title && (
          <>
            <Separator orientation="vertical" className="h-4" />
            <h1 className="text-base font-medium truncate flex-1">
              {title}
            </h1>
          </>
        )}
        
        {actions && (
          <div className={`flex items-center gap-2 ${title ? 'ml-auto' : 'ml-auto'}`}>
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AppLayout({
  children,
  headerSlot,
  navGroups,
  mode,
  preFooterSlot,
  siteHeaderTitle,
  headerActions,
}: AppLayoutProps) {
  return (
    <SidebarProvider>
      <AppSidebar
        headerSlot={headerSlot}
        navGroups={navGroups}
        mode={mode}
        preFooterSlot={preFooterSlot}
      />
      
      <SidebarInset className="flex flex-col min-h-svh bg-gray-50 min-w-0">
        <AppSiteHeader title={siteHeaderTitle} actions={headerActions} />
        
        <main className="flex-1 min-w-0 overflow-auto">
          <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 lg:px-8 py-6">
            {children}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

export default AppLayout;
